import { notification } from 'ant-design-vue'
import { createNamespacedHelpers, mapActions } from 'vuex'
import permissionMixin from './permissionMixin'

const { mapActions: modalActions } = createNamespacedHelpers('modals')

const DEEP_LINK_QUERY_KEYS = [
  'info_text',
  'tab',
  'editingAssociate',
  'openShift',
  'openPayment',
  'finalPaymentProcess',
  'time_off_id',
  'switch_project_id'
]

const DEEP_LINK_PERMISSION_ERROR =
  'You don’t have enough permissions to access this information. If you believe this is a mistake, please contact the administrator.'

const DEEP_LINK_MODAL_PERMISSIONS = {
  editingAssociate: {
    all: ['manage_associates']
  },
  openShift: {
    all: ['manage_shifts']
  },
  openPayment: {
    any: ['validate_payments', 'process_payments']
  },
  finalPaymentProcess: {
    any: ['validate_payments', 'process_payments']
  },
  time_off_id: {
    all: ['manage_shifts']
  }
}

export default {
  name: 'DeepLinkMixin',
  mixins: [permissionMixin],
  data() {
    return {
      deepLinkPermissionErrorShown: false
    }
  },
  watch: {
    $route: {
      async handler(to, _) {
        await this.handleRouteDeepLink(to)
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    ...modalActions(['openModal']),
    ...mapActions(['loadApplicationState', 'setLoading', 'switchProject']),

    async handleRouteDeepLink(to) {
      const query = { ...to.query }
      const shouldContinue = await this.handleProjectSwitchDeepLink(query)

      if (!shouldContinue) {
        return
      }

      if (this.hasDeepLinkModalQuery(query)) {
        await this.ensureApplicationStateForDeepLink()
      }

      await this.openRouteDeepLinks(query)
    },

    async handleProjectSwitchDeepLink(query) {
      const switchProjectId = this.getFirstQueryValue(query.switch_project_id)

      if (!switchProjectId) {
        return true
      }

      delete query.switch_project_id
      await this.ensureApplicationStateForDeepLink()

      if (this.isCurrentProject(switchProjectId)) {
        await this.replaceRouteQuery(query)
        return false
      }

      const targetProject = this.findAvailableProject(switchProjectId)

      if (!targetProject) {
        notification.error({
          message: 'Access Denied!',
          description:
            'Unfortunately, you don’t have enough permissions/access to view the information at this link. Please contact the sender for details.'
        })

        await this.replaceRouteQuery(this.getQueryWithoutDeepLinkParams(query))
        return false
      }

      this.setLoading(true)

      try {
        await this.switchProject({
          project_id: switchProjectId
        })
        await this.loadApplicationState()
        await this.replaceRouteQuery(query)
      } catch (error) {
        notification.error({
          message: 'Error',
          description: typeof error === 'string' ? error : error.message
        })
        await this.replaceRouteQuery(this.getQueryWithoutDeepLinkParams(query))
      } finally {
        this.setLoading(false)
      }

      return false
    },

    async ensureApplicationStateForDeepLink() {
      const applicationState = this.$store.state.applicationState

      if (!applicationState.token) {
        return
      }

      if (applicationState.currentProject && Array.isArray(applicationState.allProjects)) {
        return
      }

      this.setLoading(true)

      try {
        await this.loadApplicationState()
      } finally {
        this.setLoading(false)
      }
    },

    getFirstQueryValue(value) {
      return Array.isArray(value) ? value[0] : value
    },

    isCurrentProject(projectId) {
      return String(this.$store.state.applicationState.currentProject?.id) === String(projectId)
    },

    findAvailableProject(projectId) {
      const allProjects = this.$store.state.applicationState.allProjects

      if (!Array.isArray(allProjects)) {
        return null
      }

      return allProjects.find(
        project => String(project.id) === String(projectId) && project.is_active_associate !== false
      )
    },

    getQueryWithoutDeepLinkParams(query) {
      return Object.entries(query).reduce((result, [key, value]) => {
        if (!DEEP_LINK_QUERY_KEYS.includes(key)) {
          result[key] = value
        }

        return result
      }, {})
    },

    hasDeepLinkModalQuery(query) {
      return Object.keys(DEEP_LINK_MODAL_PERMISSIONS).some(key => !!this.getFirstQueryValue(query[key]))
    },

    async replaceRouteQuery(query) {
      await this.$router.replace({ query }).catch(() => {})
    },

    async openRouteDeepLinks(query) {
      this.deepLinkPermissionErrorShown = false

      const { info_text, tab, editingAssociate, openShift, openPayment, finalPaymentProcess, time_off_id, ...rest } =
        query

      if (info_text) {
        notification.info({
          message: 'Info',
          description: info_text
        })
      }

      if (editingAssociate && this.canOpenDeepLinkModal('editingAssociate')) {
        this.openModal({
          type: 'profile',
          data: {
            visible: true,
            associate_number: editingAssociate,
            edit: () => {},
            open_tab: tab ?? 'personal',
            is_editing: false
          }
        })
      }

      if (time_off_id && this.canOpenDeepLinkModal('time_off_id')) {
        this.openModal({
          type: 'timeOff',
          data: {
            visible: true,
            timeOffId: time_off_id,
            update: () => {}
          }
        })
      }

      if (openShift && this.canOpenDeepLinkModal('openShift')) {
        this.openModal({
          type: 'shiftView',
          data: {
            visible: true,
            edit: () => {},
            shift_id: Number(openShift),
            open_tab: 'info'
          }
        })
      }

      if (openPayment && this.canOpenDeepLinkModal('openPayment')) {
        this.openModal({
          type: 'payment',
          data: {
            id: openPayment,
            edit: () => {}
          }
        })
      }

      if (finalPaymentProcess && this.canOpenDeepLinkModal('finalPaymentProcess')) {
        this.openModal({
          type: 'finalPaycheck',
          data: {
            visible: true,
            processId: this.getFirstQueryValue(finalPaymentProcess),
            onCompleted: () => {}
          }
        })
      }

      if (info_text || tab || editingAssociate || openShift || openPayment || finalPaymentProcess || time_off_id) {
        this.$router.replace({ query: { ...rest } }).catch(() => {})
      }
    },

    canOpenDeepLinkModal(queryKey) {
      const permissions = DEEP_LINK_MODAL_PERMISSIONS[queryKey]

      if (!permissions) {
        return true
      }

      const hasRequiredPermissions =
        (!permissions.all || this.hasAllPermissions(permissions.all)) &&
        (!permissions.any || this.hasAnyPermission(permissions.any))

      if (!hasRequiredPermissions) {
        this.showDeepLinkPermissionError()
      }

      return hasRequiredPermissions
    },

    showDeepLinkPermissionError() {
      if (this.deepLinkPermissionErrorShown) {
        return
      }

      this.deepLinkPermissionErrorShown = true

      notification.error({
        message: 'Access denied',
        description: DEEP_LINK_PERMISSION_ERROR
      })
    }
  }
}
